import React, { useState, useCallback, useEffect, memo } from "react";
import { Box, Tabs, Tab, Grid, Typography, TextField, Button, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { Person as PersonIcon, Cake as CakeIcon, Wc as WcIcon, Email as EmailIcon, Phone as PhoneIcon, Home as HomeIcon, Domain as DomainIcon, Groups as GroupsIcon, LocationOn as LocationOnIcon, Save as SaveIcon, Edit as EditIcon, BusinessCenter as BusinessCenterIcon, SupervisorAccount as ManagerIcon, Badge as RoleIcon, Apartment as OrgIcon, EventAvailable as DateIcon, Fingerprint as FingerprintIcon, Bloodtype as BloodtypeIcon } from "@mui/icons-material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from "dayjs";
import { getData, putData } from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/endpoints";
import { formatDate } from "../../utils/dateFormat";

const GENDERS = ["Male", "Female", "Other"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const EditableField = memo(({ label, value, icon: Icon, onChange, isEditing, type = "text", options = [], multiline = false, readOnly = false }) => {
    const effectivelyEditing = isEditing && !readOnly;

    const renderInput = () => {
        if (type === "date") {
            return (
                <DatePicker
                    label={label}
                    format="DD/MM/YYYY"
                    value={value ? dayjs(value) : null}
                    disableFuture
                    onChange={(newValue) => onChange(newValue ? newValue.format('YYYY-MM-DD') : "")}
                    slotProps={{
                        textField: {
                            size: 'small',
                            fullWidth: true,
                            variant: "standard",
                            placeholder: `Select ${label}`
                        }
                    }}
                />
            );
        }

        return (
            <TextField
                fullWidth
                label={label}
                select={type === "select"}
                variant="standard"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                size="small"
                multiline={multiline}
                rows={multiline ? 3 : 1}
                placeholder={`Enter ${label}`}
                InputLabelProps={{ shrink: true }}
            >
                {type === "select" && options.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
            </TextField>
        );
    };

    return (
        <Box sx={{ mb: 3, display: 'flex', alignItems: multiline && !effectivelyEditing ? 'flex-start' : 'center' }}>
            <Icon sx={{ color: '#5f6368', mr: 2, fontSize: 22, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                {!effectivelyEditing && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                        {label}
                    </Typography>
                )}
                {effectivelyEditing ? renderInput() : (
                    <Typography variant="body1" sx={{ fontWeight: 500, color: readOnly && isEditing ? '#9aa0a6' : '#202124' }}>
                        {value || 'Not Provided'}
                    </Typography>
                )}
            </Box>
        </Box>
    );
});

function MyProfilePage() {
    const [userData] = useState(() => JSON.parse(localStorage.getItem("userData") || "{}"));
    const [userDetails, setUserDetails] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [isGlobalEditing, setIsGlobalEditing] = useState(false);

    // Track only modified fields
    const [changes, setChanges] = useState({});

    // Navigation Protection States
    const [pendingTab, setPendingTab] = useState(null);
    const [showDialog, setShowDialog] = useState(false);

    const isDirty = Object.keys(changes).length > 0;
    const isEditButtonVisible = tabValue !== 2;

    const fetchUserDetails = useCallback(async () => {
        if (!userData.id) return;
        try {
            const response = await getData(`${API_ENDPOINTS.fetchUserDetails}?userId=${userData.id}`);
            if (response.status === "SUCCESS") {
                setUserDetails(response.message.user);
                setChanges({}); // Reset changes on fresh fetch
            }
        } catch (error) {
            return;
        }
    }, [userData.id]);

    useEffect(() => {
        fetchUserDetails();
    }, [fetchUserDetails]);

    const handleFieldChange = (field, newValue) => {
        if (field === 'aadhaar_number') {
            const numericValue = newValue.replace(/\D/g, ""); // Remove non-digits
            if (numericValue.length > 12) return; // Prevent more than 12 digits
            newValue = numericValue;
        }
        setUserDetails(prev => ({ ...prev, [field]: newValue }));

        // Track the change for the payload
        setChanges(prev => ({ ...prev, [field]: newValue }));
    };

    const handleSave = async (targetTab = null) => {
        if (!isDirty) {
            setIsGlobalEditing(false);
            if (targetTab !== null) setTabValue(targetTab);
            return;
        }

        try {
            // ONLY sending the 'changes' object as payload
            const response = await putData(API_ENDPOINTS.updateEmployeeProfile(userData.id), changes);

            if (response.status === "SUCCESS") {
                setIsGlobalEditing(false);
                setChanges({}); // Clear changes after successful save
                setShowDialog(false);
                if (targetTab !== null) {
                    setTabValue(targetTab);
                }
            }
        } catch (error) {
            return;
        }
    };

    const handleHeaderButtonClick = () => {
        if (isGlobalEditing) {
            handleSave();
        } else {
            setIsGlobalEditing(true);
        }
    };

    const handleTabChange = (event, newValue) => {
        if (isDirty) {
            setPendingTab(newValue);
            setShowDialog(true);
        } else {
            setTabValue(newValue);
            setIsGlobalEditing(false);
        }
    };

    const confirmDiscard = () => {
        setChanges({});
        setIsGlobalEditing(false);
        setTabValue(pendingTab);
        setShowDialog(false);
        fetchUserDetails(); // Re-fetch to clear unsaved UI changes
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', pr: 2 }}>
                    <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                        <Tab label="Personal Details" sx={{ fontWeight: 600, textTransform: 'none' }} />
                        <Tab label="Contact Information" sx={{ fontWeight: 600, textTransform: 'none' }} />
                        <Tab label="Work Details" sx={{ fontWeight: 600, textTransform: 'none' }} />
                    </Tabs>

                    {isEditButtonVisible && (
                        <Button
                            variant={isGlobalEditing ? "contained" : "outlined"}
                            startIcon={isGlobalEditing ? <SaveIcon /> : <EditIcon />}
                            size="small"
                            sx={{ textTransform: 'none', borderRadius: '20px', fontWeight: 600, my: 1 }}
                            onClick={handleHeaderButtonClick}
                        >
                            {isGlobalEditing ? "Save Changes" : tabValue === 0 ? "Edit Personal" : "Edit Contact"}
                        </Button>
                    )}
                </Box>

                {/* Tab 0: Personal */}
                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <EditableField label="First Name" value={userDetails?.first_name} icon={PersonIcon} isEditing={isGlobalEditing} onChange={(v) => handleFieldChange('first_name', v)} />
                            <EditableField label="Last Name" value={userDetails?.last_name} icon={PersonIcon} isEditing={isGlobalEditing} onChange={(v) => handleFieldChange('last_name', v)} />
                            <EditableField label="Date of Birth" value={formatDate(userDetails?.dob)} icon={CakeIcon} type="date" isEditing={isGlobalEditing} onChange={(v) => handleFieldChange('dob', v)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <EditableField label="Gender" value={userDetails?.gender} icon={WcIcon} type="select" options={GENDERS} isEditing={isGlobalEditing} onChange={(v) => handleFieldChange('gender', v)} />
                            <EditableField label="Blood Group" value={userDetails?.blood_group} icon={BloodtypeIcon} type="select" options={BLOOD_GROUPS} isEditing={isGlobalEditing} onChange={(v) => handleFieldChange('blood_group', v)} />
                            <EditableField label="Aadhaar Number" value={userDetails?.aadhaar_number} icon={FingerprintIcon} isEditing={isGlobalEditing} onChange={(v) => handleFieldChange('aadhaar_number', v)} />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab 1: Contact */}
                <TabPanel value={tabValue} index={1}>
                    <Grid container spacing={4}>
                        {/* Left Column: Basic Contact */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <EditableField
                                label="Work Email"
                                value={userDetails?.email}
                                icon={EmailIcon}
                                readOnly
                                isEditing={isGlobalEditing}
                            />
                            <EditableField
                                label="Personal Email"
                                value={userDetails?.personal_email}
                                icon={EmailIcon}
                                isEditing={isGlobalEditing}
                                onChange={(v) => handleFieldChange('personal_email', v)}
                            />
                            <EditableField
                                label="Phone Number"
                                value={userDetails?.phone_number}
                                icon={PhoneIcon}
                                isEditing={isGlobalEditing}
                                onChange={(v) => handleFieldChange('phone_number', v)}
                            />
                            <EditableField
                                label="Address"
                                value={userDetails?.addresses?.[0] || ""}
                                icon={HomeIcon}
                                multiline
                                isEditing={isGlobalEditing}
                                onChange={(v) => handleFieldChange('addresses', [v])}
                            />
                        </Grid>

                        {/* Right Column: Emergency Contact */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <EditableField
                                label="Emergency Contact"
                                value={userDetails?.emergency_contact}
                                icon={PhoneIcon} // You could also use ContactPhoneIcon
                                isEditing={isGlobalEditing}
                                onChange={(v) => handleFieldChange('emergency_contact', v)}
                            />
                            <EditableField
                                label="Emergency Contact Relationship"
                                value={userDetails?.emergency_contact_relationship}
                                icon={GroupsIcon}
                                type="select"
                                options={["Parent", "Spouse", "Sibling", "Friend", "Other"]}
                                isEditing={isGlobalEditing}
                                onChange={(v) => handleFieldChange('emergency_contact_relationship', v)}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab 2: Work (Read Only) */}
                <TabPanel value={tabValue} index={2}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <EditableField label="Employee ID" value={userDetails?.emp_id} icon={BusinessCenterIcon} readOnly isEditing={isGlobalEditing} />
                            <EditableField label="Organization" value={userDetails?.organization_name} icon={OrgIcon} readOnly isEditing={isGlobalEditing} />
                            <EditableField label="Department" value={userDetails?.department} icon={DomainIcon} readOnly isEditing={isGlobalEditing} />
                            <EditableField label="Team" value={userDetails?.team} icon={GroupsIcon} readOnly isEditing={isGlobalEditing} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <EditableField label="Role / Designation" value={userDetails?.designation} icon={RoleIcon} readOnly isEditing={isGlobalEditing} />
                            <EditableField label="Manager" value={userDetails?.report_manager_first_name ? `${userDetails.report_manager_first_name} ${userDetails.report_manager_last_name || ''}` : 'Not Assigned'} icon={ManagerIcon} readOnly isEditing={isGlobalEditing} />
                            <EditableField label="Work Location" value={userDetails?.location_name} icon={LocationOnIcon} readOnly isEditing={isGlobalEditing} />
                            <EditableField label="Date of Joining" value={userDetails?.doj ? dayjs(userDetails.doj).format('DD/MM/YYYY') : ''} icon={DateIcon} readOnly isEditing={isGlobalEditing} />
                        </Grid>
                    </Grid>
                </TabPanel>

                <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
                    <DialogTitle>Unsaved Changes</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            You have modified fields on this tab. How would you like to proceed?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={confirmDiscard} color="error" sx={{ textTransform: 'none' }}>
                            Discard Changes
                        </Button>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button onClick={() => setShowDialog(false)} sx={{ textTransform: 'none' }}>
                            Cancel
                        </Button>
                        <Button onClick={() => handleSave(pendingTab)} variant="contained" sx={{ textTransform: 'none' }}>
                            Save & Switch
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
}

export default MyProfilePage;